import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import { FiX, FiCheck, FiAward, FiLoader, FiStar } from 'react-icons/fi';
import { useAuth } from '../contexts/AuthContext';

const SubscriptionModal = ({ isOpen, onClose }) => {
  const { upgradeToProfessional, loading } = useAuth();
  const [isProcessing, setIsProcessing] = useState(false);

  const handleUpgrade = async () => {
    setIsProcessing(true);
    try {
      const result = await upgradeToProfessional();
      if (result.success) {
        onClose();
        // Show success message
        setTimeout(() => {
          alert('🎉 Welcome to Professional! You now have unlimited projects and premium features.');
        }, 500);
      } else {
        alert('Payment failed. Please try again.');
      }
    } catch (error) {
      alert('Payment error. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  if (!isOpen) return null;

  const features = [
    'Unlimited projects',
    'Advanced material specifications',
    'Priority customer support',
    'Export to multiple formats',
    'Team collaboration features',
    'Custom branding options',
    'Advanced reporting',
    'API access'
  ];

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0, y: 20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.9, opacity: 0, y: 20 }}
          className="w-full max-w-lg bg-white rounded-3xl shadow-2xl overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="relative p-6 bg-gradient-to-r from-yellow-400 to-orange-500 text-white">
            <button
              onClick={onClose}
              className="absolute top-4 right-4 p-2 text-white/80 hover:text-white rounded-full hover:bg-white/10 transition-colors"
            >
              <SafeIcon icon={FiX} size={20} />
            </button>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <SafeIcon icon={FiAward} size={32} />
              </div>
              <h2 className="text-2xl font-bold mb-2">Upgrade to Professional</h2>
              <p className="text-yellow-100">
                Unlock unlimited projects and premium features
              </p>
            </div>
          </div>

          {/* Pricing */}
          <div className="p-6">
            <div className="text-center mb-6">
              <div className="flex items-center justify-center gap-2 mb-2">
                <span className="text-3xl font-bold text-gray-900">₱399</span>
                <span className="text-gray-500">/month</span>
              </div>
              <div className="flex items-center justify-center gap-1 text-yellow-500 mb-2">
                {[...Array(5)].map((_, i) => (
                  <SafeIcon key={i} icon={FiStar} className="fill-current" size={16} />
                ))}
              </div>
              <p className="text-sm text-gray-600">Trusted by 500+ construction professionals</p>
            </div>

            {/* Features */}
            <div className="space-y-3 mb-6">
              <h3 className="font-semibold text-gray-900 text-center mb-4">Everything in Professional:</h3>
              {features.map((feature, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="flex items-center gap-3"
                >
                  <div className="w-5 h-5 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <SafeIcon icon={FiCheck} className="text-green-600" size={12} />
                  </div>
                  <span className="text-sm text-gray-700">{feature}</span>
                </motion.div>
              ))}
            </div>

            {/* CTA */}
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={handleUpgrade}
              disabled={isProcessing || loading}
              className="w-full py-4 bg-gradient-to-r from-yellow-400 to-orange-500 text-white rounded-xl font-bold text-lg hover:shadow-lg transition-all disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {isProcessing || loading ? (
                <>
                  <SafeIcon icon={FiLoader} className="animate-spin" />
                  <span>Processing...</span>
                </>
              ) : (
                <>
                  <SafeIcon icon={FiAward} />
                  <span>Upgrade Now</span>
                </>
              )}
            </motion.button>

            <div className="text-center mt-4 space-y-2">
              <p className="text-xs text-gray-500">
                Secure payment • Cancel anytime • 30-day money-back guarantee
              </p>
              <p className="text-xs text-gray-500">
                By upgrading, you agree to our Terms of Service and Privacy Policy
              </p>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default SubscriptionModal;